
import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getThumbnailUrl, handleImageError, ImageErrorState } from '@/utils/imageUtils';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { motion, AnimatePresence } from 'framer-motion';
import VerifiedBadge from '@/components/common/VerifiedBadge';

interface BulletinAuthorProps {
  username: string;
  profileImageUrl?: string;
  isAdmin?: boolean;
  isPremium?: boolean;
  isPartner?: boolean;
  isVisible?: boolean;
}

const BulletinAuthor = ({ username, profileImageUrl, isAdmin, isPremium, isPartner, isVisible = true }: BulletinAuthorProps) => {
  const [errorState, setErrorState] = React.useState<ImageErrorState>({ retryCount: 0, hasError: false });
  
  if (!username || !isVisible) return null;

  const avatarUrl = getThumbnailUrl(profileImageUrl, 48);
  
  return (
    <ErrorBoundary>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          <Link 
            to={`/profile/${username}`}
            className="flex items-center gap-1.5 group/profile hover:opacity-80 transition-opacity"
          >
            <div className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-full transition-all duration-300",
              "bg-black/60 backdrop-blur-sm",
              "sm:px-2.5 sm:py-1.5",
              isAdmin && "ring-2 ring-[#F3BA2F]/30",
              isPremium && "ring-2 ring-purple-500/30"
            )}>
              <Avatar className={cn(
                "h-5 w-5 sm:h-6 sm:w-6 transition-transform duration-300 hover:scale-105",
                isAdmin && "ring-2 ring-[#F97316]",
                isPremium && "ring-2 bg-gradient-to-r from-[#1A1F2C] via-[#7E69AB] to-[#FFD700] p-[2px]",
                !isAdmin && !isPremium && "ring-1 ring-primary/20"
              )}>
                {!errorState.hasError && avatarUrl && (
                  <AvatarImage 
                    src={avatarUrl}
                    alt={username}
                    className={cn(
                      "object-cover",
                      isPremium && "rounded-full"
                    )}
                    onError={(e) => {
                      handleImageError(e, setErrorState, errorState, avatarUrl);
                    }}
                  />
                )}
                <AvatarFallback className={cn(
                  "bg-primary text-primary-foreground text-[10px]",
                  isPremium && "bg-gradient-to-r from-[#1A1F2C] to-[#7E69AB]"
                )}>
                  {profileImageUrl ? '' : username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-[10px] sm:text-xs font-medium text-white flex items-center gap-1.5">
                @{username}
                {isAdmin && (
                  <VerifiedBadge type="admin" size={16} className="inline-block" />
                )}
                {isPartner && (
                  <VerifiedBadge type="partner" size={16} className="inline-block" />
                )}
              </span>
            </div>
          </Link>
        </motion.div>
      </AnimatePresence>
    </ErrorBoundary>
  );
};

export default BulletinAuthor;
